import React, { useState } from "react";
import {
  Search, Plus, ShieldCheck, ExternalLink, Sparkles, X, Trash2, Eye, Send, Globe,
  RefreshCw, CheckCircle2, AlertTriangle, CircleDot, Edit3, Save
} from "lucide-react";
import {
  SeoMarket, ContentItem, ContentStatus, ContentType, PusherSettings, SeoSwitches,
  TransitionResult, IndexingResult, InspectResult, SeoContentDetail, QualityReport
} from "./types";
import { STATUS_LABEL, fmtDateTime, MARKET_TZ } from "./engineFormat";

// Mirrors ALLOWED in lambda_extract/seo_engine/smart_seo_publisher.py
const ALLOWED: Record<ContentStatus, ContentStatus[]> = {
  idea: ["draft", "archived"],
  draft: ["idea", "review", "archived"],
  review: ["draft", "ready_buffer", "archived"],
  ready_buffer: ["review", "archived"],
  scheduled: ["ready_buffer", "review"],
  publishing: ["ready_buffer"],
  published: ["archived"],
  failed: ["ready_buffer", "review", "archived"],
  archived: ["idea", "draft"],
};

const TYPE_LABEL: Partial<Record<ContentType, string>> = {
  guide: "Guide", pillar: "Pillar", news: "Nyhed", nyhed: "Nyhed", lender_review: "Anmeldelse",
  "lender-review": "Anmeldelse", amount_page: "Beløbsside", belopp: "Beløbsside", comparison: "Sammenligning",
  samla: "Samlelån", programmatic: "Programmatisk",
};

const STATUS_STYLE: Partial<Record<ContentStatus, string>> = {
  ready_buffer: "bg-emerald-50 text-emerald-800 border-emerald-200",
  scheduled: "bg-blue-50 text-blue-800 border-blue-200",
  publishing: "bg-blue-50 text-blue-800 border-blue-200",
  review: "bg-purple-50 text-purple-800 border-purple-200",
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  published: "bg-green-100 text-green-900 border-green-300",
  failed: "bg-red-50 text-red-800 border-red-200",
};

export interface ContentEngine {
  switches: SeoSwitches | null;
  transition: (id: string, to: ContentStatus, opts?: { factChecked?: boolean; note?: string }) => Promise<TransitionResult>;
  create: (payload: { title: string; targetKeyword: string; type: ContentType; slug?: string; searchVolume?: number;
                      notes?: string }) => Promise<void>;
  update?: (id: string, patch: Record<string, any>) => Promise<any>;
  qualityCheck?: (id: string) => Promise<QualityReport>;
  openGenerate: () => void;
  publishNow: (id: string) => Promise<string>;
  submitUrl: (id: string) => Promise<IndexingResult>;
  inspectUrl: (id: string) => Promise<InspectResult>;
  loadDetail: (id: string) => Promise<SeoContentDetail>;
}

interface SeoContentTabProps {
  market: SeoMarket;
  contentList: ContentItem[];
  setContentList: React.Dispatch<React.SetStateAction<ContentItem[]>>;
  settings: PusherSettings;
  onTriggerPush: () => void;
  isDeploying: boolean;
  engine?: ContentEngine;
}

function errorPayload(e: unknown): { message: string; quality?: QualityReport } {
  const err = e as { message?: string; payload?: { error?: string; quality?: QualityReport } };
  return { message: err?.payload?.error || err?.message || "Kaldet fejlede", quality: err?.payload?.quality };
}

function QualityList({ report, onlyProblems = false }: { report: QualityReport; onlyProblems?: boolean }) {
  const checks = onlyProblems ? report.checks.filter((c) => c.status !== "pass") : report.checks;
  return (
    <ul className="space-y-1.5 text-xs">
      {checks.map((c) => (
        <li key={c.id} className="flex items-start gap-2">
          {c.status === "pass" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
            : c.status === "fail" ? <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
            : <CircleDot className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />}
          <span>
            <span className={c.status === "fail" ? "font-semibold text-red-800" : "text-gray-800"}>{c.label}</span>
            <span className="text-gray-500"> · {c.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function SeoContentTab({
  market,
  contentList,
  setContentList,
  settings,
  onTriggerPush,
  isDeploying,
  engine
}: SeoContentTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "error" | "info"; text: string } | null>(null);
  const [approveItem, setApproveItem] = useState<ContentItem | null>(null);
  const [approveChecked, setApproveChecked] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [approveReport, setApproveReport] = useState<QualityReport | null>(null);
  const [detail, setDetail] = useState<SeoContentDetail | null>(null);
  const [detailTab, setDetailTab] = useState<"article" | "quality">("article");
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [editDraft, setEditDraft] = useState<{
    title: string;
    slug: string;
    targetKeyword: string;
    metaTitle: string;
    metaDescription: string;
    excerpt: string;
    body: Record<string, any>;
  } | null>(null);

  // New Article Form state
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newKeyword, setNewKeyword] = useState("");
  const [newVolume, setNewVolume] = useState<number>(3000);
  const [newType, setNewType] = useState<ContentType>("guide");
  const [newEditLevel, setNewEditLevel] = useState<ContentItem["aiEditLevel"]>("medium");
  const [newAuthor, setNewAuthor] = useState("SimplyLoan Redaktion");
  const [newNotes, setNewNotes] = useState("");

  const marketList = contentList.filter(c => c.market === market);
  const filteredList = marketList
    .filter(c => filterStatus === "all" || c.status === filterStatus)
    .filter(c => filterType === "all" || c.type === filterType)
    .filter(c =>
      !searchQuery ||
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.targetKeyword.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const count = (s: ContentStatus) => marketList.filter(c => c.status === s).length;

  const resetForm = () => {
    setNewTitle("");
    setNewSlug("");
    setNewKeyword("");
    setNewNotes("");
  };

  const handleCreateArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    if (engine) {
      try {
        await engine.create({ title: newTitle.trim(), targetKeyword: newKeyword.trim() || newTitle.trim(), type: newType,
                              slug: newSlug.trim() || undefined, searchVolume: Number(newVolume) || undefined,
                              notes: newNotes.trim() || undefined });
        setIsAddModalOpen(false);
        resetForm();
        setNotice({ kind: "ok", text: "Idéen er oprettet. Agenten eller en redaktør kan nu skrive den." });
      } catch (err) {
        setNotice({ kind: "error", text: errorPayload(err).message });
      }
      return;
    }

    const generatedSlug = newSlug.trim() || newTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

    const newItem: ContentItem = {
      id: `sl-${Date.now()}`,
      market,
      title: newTitle.trim(),
      slug: generatedSlug,
      type: newType,
      status: "ready_buffer", // Directly into buffer if desired, or review
      targetKeyword: newKeyword.trim() || newTitle.trim(),
      searchVolume: Number(newVolume) || 1000,
      wordCount: 1500,
      scheduledDate: null,
      scheduledTimeWindow: null,
      author: newAuthor.trim() || "SimplyLoan Redaktion",
      factChecked: true,
      aiEditLevel: newEditLevel,
      internalLinksCount: 4,
      notes: newNotes.trim()
    };

    setContentList(prev => [newItem, ...prev]);
    setIsAddModalOpen(false);
    resetForm();
  };

  const runEngine = async (id: string, fn: () => Promise<string | void>) => {
    setBusyId(id);
    setNotice(null);
    try {
      const text = await fn();
      if (text) setNotice({ kind: "ok", text });
    } catch (err) {
      setNotice({ kind: "error", text: errorPayload(err).message });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = (item: ContentItem) => {
    if (engine) {
      if (!window.confirm(`Arkivér «${item.title}»?${item.status === "published" ? " Siden forsvinder fra sitet ved næste deploy – tilføj en 301." : ""}`)) return;
      runEngine(item.id, async () => { await engine.transition(item.id, "archived"); return "Arkiveret."; });
      return;
    }
    if (window.confirm("Er du sikker på at du vil fjerne denne artikel fra pipelinen?")) {
      setContentList(prev => prev.filter(c => c.id !== item.id));
    }
  };

  const handleStatusChange = (item: ContentItem, newStatus: ContentStatus) => {
    if (engine) {
      if (newStatus === "ready_buffer" && (item.status === "review" || item.status === "failed")) {
        openApprove(item);
        return;
      }
      if (newStatus === "archived") {
        handleDelete(item);
        return;
      }
      runEngine(item.id, async () => {
        await engine.transition(item.id, newStatus);
        return `Status ændret til ${STATUS_LABEL[newStatus]}.`;
      });
      return;
    }
    setContentList(prev => prev.map(c => {
      if (c.id === item.id) {
        return {
          ...c,
          status: newStatus,
          factChecked: newStatus === "ready_buffer" || newStatus === "published" ? true : c.factChecked
        };
      }
      return c;
    }));
  };

  const openApprove = (item: ContentItem) => {
    setApproveItem(item);
    setApproveChecked(false);
    setApproveNote("");
    setApproveReport(null);
  };

  const submitApprove = async () => {
    if (!engine || !approveItem) return;
    setBusyId(approveItem.id);
    try {
      const res = await engine.transition(approveItem.id, "ready_buffer", { factChecked: approveChecked, note: approveNote || undefined });
      setApproveItem(null);
      setNotice({ kind: "ok", text: res.fast_lane?.ok
        ? `Godkendt. Nyheden er lagt i hurtigsporet: ${fmtDateTime(res.fast_lane.planned_at, MARKET_TZ[market])}.`
        : "Godkendt og lagt i bufferen. Pusheren tildeler et tidspunkt ved næste kørsel." });
    } catch (err) {
      const { message, quality } = errorPayload(err);
      if (quality) setApproveReport(quality);
      else setNotice({ kind: "error", text: message });
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = (item: ContentItem) => {
    if (!engine) return;
    runEngine(item.id, async () => {
      const loaded = await engine.loadDetail(item.id);
      setDetail(loaded);
      setDetailTab("article");
      setIsEditingDetail(false);
      setEditDraft(null);
    });
  };

  const startEditingDetail = (d: SeoContentDetail) => {
    setEditDraft({
      title: d.title || "",
      slug: d.slug || "",
      targetKeyword: d.targetKeyword || "",
      metaTitle: d.metaTitle || "",
      metaDescription: d.metaDescription || "",
      excerpt: d.excerpt || "",
      body: JSON.parse(JSON.stringify(d.body || { sections: [], faqs: [], takeaways: [], sources: [] })),
    });
    setIsEditingDetail(true);
  };

  const saveDetailChanges = async () => {
    if (!engine?.update || !detail || !editDraft) return;
    setBusyId(detail.id);
    try {
      await engine.update(detail.id, {
        title: editDraft.title,
        slug: editDraft.slug,
        targetKeyword: editDraft.targetKeyword,
        metaTitle: editDraft.metaTitle,
        metaDescription: editDraft.metaDescription,
        excerpt: editDraft.excerpt,
        body: editDraft.body,
      });
      const refreshed = await engine.loadDetail(detail.id);
      setDetail(refreshed);
      setIsEditingDetail(false);
      setEditDraft(null);
      setNotice({ kind: "ok", text: "Artiklen er gemt og kvalitetstjekket er opdateret." });
    } catch (err) {
      setNotice({ kind: "error", text: errorPayload(err).message });
    } finally {
      setBusyId(null);
    }
  };

  const rerunQualityCheck = async () => {
    if (!engine?.qualityCheck || !detail) return;
    setBusyId(detail.id);
    try {
      const report = await engine.qualityCheck(detail.id);
      setDetail({ ...detail, quality: report, qualityScore: report.score, blocking: report.blocking });
      setNotice({ kind: "ok", text: `Kvalitetstjek kørt: ${report.score} % (${report.blocking.length} blokerende).` });
    } catch (err) {
      setNotice({ kind: "error", text: errorPayload(err).message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">

      {notice && (
        <div className={`p-3.5 rounded-xl border text-sm flex items-start justify-between gap-3 ${
          notice.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : notice.kind === "error" ? "bg-red-50 border-red-200 text-red-900" : "bg-slate-50 border-slate-200 text-slate-800"}`}>
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="text-current opacity-60 hover:opacity-100" aria-label="Luk">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top Controls Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">

        {/* Search */}
        <div className="flex items-center gap-2 flex-1 max-w-md bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Søg i artikler, mål-søgeord..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-sm bg-transparent border-0 focus:outline-none p-0 text-gray-800 placeholder-gray-400"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filters and New Article CTA */}
        <div className="flex flex-wrap items-center gap-2.5">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 font-medium text-gray-700 focus:outline-none"
          >
            <option value="all">Alle statusser ({marketList.length})</option>
            <option value="review">Til review ({count("review")})</option>
            <option value="ready_buffer">I buffer ({count("ready_buffer")})</option>
            <option value="scheduled">Planlagt ({count("scheduled")})</option>
            {engine && <option value="publishing">Udgives ({count("publishing")})</option>}
            <option value="draft">Kladde ({count("draft")})</option>
            <option value="idea">Idé ({count("idea")})</option>
            <option value="published">Live ({count("published")})</option>
            {engine && <option value="failed">Fejlet ({count("failed")})</option>}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 font-medium text-gray-700 focus:outline-none"
          >
            <option value="all">Alle typer</option>
            {engine ? (
              <>
                <option value="guide">Guide</option>
                <option value="pillar">Pillar</option>
                <option value="news">Nyhed</option>
                <option value="lender_review">Anmeldelse</option>
                <option value="amount_page">Beløbsside</option>
                <option value="comparison">Sammenligning</option>
              </>
            ) : (
              <>
                <option value="guide">Guide</option>
                <option value="samla">Samlelån</option>
                <option value="belopp">Beløbsside</option>
                <option value="lender-review">Långiver Review</option>
                <option value="programmatic">Programmatisk</option>
              </>
            )}
          </select>

          {engine && (
            <button
              onClick={engine.openGenerate}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-[#0F3F34]/30 hover:bg-[#0F3F34]/5 text-[#0F3F34] text-xs font-bold rounded-xl transition"
            >
              <Sparkles className="w-4 h-4" />
              <span>Generér artikel nu</span>
            </button>
          )}

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white text-xs font-bold rounded-xl transition shadow-xs"
          >
            <Plus className="w-4 h-4" />
            <span>{engine ? "Opret idé" : "Opret artikel"}</span>
          </button>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50/80 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="py-3.5 px-5">Artikel & Mål-Søgeord</th>
                <th className="py-3.5 px-4">Type</th>
                <th className="py-3.5 px-4">Søgevolumen</th>
                <th className="py-3.5 px-4">{engine ? "Kvalitet" : "AI / Menneske Polish"}</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-5 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-400">
                    Ingen artikler matcher de valgte filtre i {market}.
                  </td>
                </tr>
              ) : (
                filteredList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">

                    {/* Title and Keyword */}
                    <td className="py-4 px-5">
                      {engine ? (
                        <button
                          type="button"
                          onClick={() => openDetail(item)}
                          className="font-bold text-gray-900 hover:text-[#0F3F34] text-left leading-snug transition-colors"
                        >
                          {item.title}
                        </button>
                      ) : (
                        <div className="font-bold text-gray-900 leading-snug">{item.title}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1 flex flex-wrap items-center gap-2">
                        <span>Søgeord: <strong className="text-gray-700">{item.targetKeyword}</strong></span>
                        <span>•</span>
                        <span className="font-mono text-gray-500">/{item.slug}</span>
                        {item.wordCount > 0 && (
                          <>
                            <span>•</span>
                            <span>{item.wordCount} ord</span>
                          </>
                        )}
                        {item.scheduledDate && (item.status === "scheduled" || item.status === "publishing") && (
                          <>
                            <span>•</span>
                            <span className="text-blue-700">{item.scheduledDate} kl. {item.scheduledTimeWindow}</span>
                          </>
                        )}
                      </div>
                      {item.lastError && <div className="text-[11px] text-red-700 mt-1">{item.lastError}</div>}
                    </td>

                    {/* Type */}
                    <td className="py-4 px-4">
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
                        {TYPE_LABEL[item.type] || item.type}
                      </span>
                    </td>

                    {/* Volume */}
                    <td className="py-4 px-4 font-semibold text-gray-800">
                      {item.searchVolume ? `${item.searchVolume.toLocaleString('da-DK')} / md.` : "–"}
                    </td>

                    {/* Quality (engine) or polish level (local) */}
                    <td className="py-4 px-4">
                      {engine ? (
                        <div className="flex items-center gap-2 text-xs">
                          {item.qualityScore != null ? (
                            <span className={`font-bold ${item.blocking && item.blocking.length ? "text-amber-700" : "text-emerald-700"}`}>
                              {item.qualityScore} %
                            </span>
                          ) : <span className="text-gray-400">–</span>}
                          {item.blocking && item.blocking.length > 0 && (
                            <span className="text-amber-700">{item.blocking.length} blokerer</span>
                          )}
                          {item.factChecked && (
                            <span title={`Godkendt af ${item.reviewer || "redaktør"}`}>
                              <ShieldCheck className="w-4 h-4 text-emerald-600" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            item.aiEditLevel === 'heavy'
                              ? 'bg-purple-100 text-purple-800'
                              : item.aiEditLevel === 'medium'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.aiEditLevel === 'heavy' ? 'Heavy Polish' : item.aiEditLevel === 'medium' ? 'Standard Polish' : 'Light Edit'}
                          </span>
                          {item.factChecked && (
                            <span title="Faktatjekket & godkendt">
                              <ShieldCheck className="w-4 h-4 text-emerald-600" />
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Status with inline changer */}
                    <td className="py-4 px-4">
                      <select
                        value={item.status}
                        disabled={busyId === item.id}
                        onChange={(e) => handleStatusChange(item, e.target.value as ContentStatus)}
                        className={`text-xs font-bold rounded-lg px-2.5 py-1 border transition focus:outline-none ${
                          STATUS_STYLE[item.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                      >
                        <option value={item.status}>{STATUS_LABEL[item.status] || item.status}</option>
                        {(engine ? ALLOWED[item.status] || [] : (["idea", "draft", "review", "ready_buffer", "scheduled", "published"] as ContentStatus[])
                          .filter((s) => s !== item.status)).map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {busyId === item.id && <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />}

                        {engine && item.status === "review" && (
                          <button onClick={() => openApprove(item)}
                                  className="px-2.5 py-1 text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition">
                            Godkend
                          </button>
                        )}

                        {engine && (item.status === "ready_buffer" || item.status === "scheduled") && (
                          <button
                            onClick={() => window.confirm(`Udgiv «${item.title}» nu, uden for kalenderen?`) &&
                              runEngine(item.id, () => engine.publishNow(item.id))}
                            className="p-1.5 text-gray-400 hover:text-[#0F3F34] hover:bg-emerald-50 rounded-lg transition"
                            title="Udgiv nu"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}

                        {item.publishedUrl ? (
                          <a href={item.publishedUrl} target="_blank" rel="noreferrer"
                             className="p-1.5 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition"
                             title="Se live artikel på SimplyLoan">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        ) : !engine ? (
                          <button
                            onClick={() => handleStatusChange(item, "ready_buffer")}
                            className="px-2.5 py-1 text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition"
                            title="Godkend direkte til buffer"
                          >
                            Til buffer
                          </button>
                        ) : null}

                        {engine && item.status === "published" && (
                          <>
                            <button
                              onClick={() => runEngine(item.id, async () => {
                                const r = await engine.submitUrl(item.id);
                                if (r.status === "dry_run") return "Indeksering er slået fra (SEO_INDEXING_MODE) – intet sendt.";
                                return `IndexNow: ${r.indexnow?.status ?? "–"} · Sitemap i Search Console: ${r.sitemap?.status ?? "–"}`;
                              })}
                              className="p-1.5 text-gray-400 hover:text-[#0F3F34] hover:bg-emerald-50 rounded-lg transition"
                              title="Send til IndexNow og genindsend sitemap"
                            >
                              <Globe className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => runEngine(item.id, async () => {
                                const r = await engine.inspectUrl(item.id);
                                return r.status === "ok"
                                  ? `Google: ${r.coverage_state || r.verdict} · sidst crawlet ${fmtDateTime(r.last_crawl_time)}`
                                  : `URL Inspection fejlede: ${r.error || r.status}`;
                              })}
                              className="px-2 py-1 text-[11px] font-bold text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                              title="Indekseringsstatus i Google (URL Inspection API)"
                            >
                              Google-status
                            </button>
                          </>
                        )}

                        {engine && (
                          <button onClick={() => openDetail(item)}
                                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Læs & redigér artikel">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title={engine ? "Arkivér" : "Slet artikel"}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: approve into the buffer (engine) */}
      {approveItem && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-base">Godkend til buffer</h3>
              <button onClick={() => setApproveItem(null)} className="text-gray-400 hover:text-gray-600" aria-label="Luk">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm font-semibold text-gray-900">{approveItem.title}</p>
            <p className="text-xs text-gray-600">
              Kvalitetstjekket kører igen nu og ved udgivelse. Din godkendelse gemmes med dit navn som reviewer.
            </p>
            <label className="flex items-start gap-2 text-xs text-gray-800">
              <input type="checkbox" className="mt-0.5" checked={approveChecked} onChange={(e) => setApproveChecked(e.target.checked)} />
              <span>Jeg har læst artiklen og kontrolleret tal, renter, vilkår og kilder. Den indeholder ingen løfter om
                hurtige eller sikre lån.</span>
            </label>
            <textarea rows={2} value={approveNote} onChange={(e) => setApproveNote(e.target.value)}
                      placeholder="Note til loggen (valgfri)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs" />
            {approveReport && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 space-y-2">
                <div className="text-xs font-bold text-red-900">Kvalitetstjekket blokerer ({approveReport.score} %):</div>
                <QualityList report={approveReport} onlyProblems />
              </div>
            )}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2 text-xs">
              <button onClick={() => setApproveItem(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50">
                Annuller
              </button>
              <button onClick={submitApprove} disabled={!approveChecked || busyId === approveItem.id}
                      className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl disabled:opacity-50">
                Godkend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: article detail & inline editor (engine) */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full p-6 space-y-4 shadow-xl border border-gray-100 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[11px] font-bold rounded-lg px-2 py-0.5 border ${STATUS_STYLE[detail.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                    {STATUS_LABEL[detail.status] || detail.status}
                  </span>
                  {detail.quality && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${detail.quality.blocking.length ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
                      Kvalitet: {detail.quality.score} % ({detail.quality.word_count} ord)
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-gray-900 text-lg">{detail.title}</h3>
                <a href={detail.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-gray-500 hover:underline inline-flex items-center gap-1">
                  {detail.url} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {engine?.update && !isEditingDetail && (
                  <button
                    onClick={() => startEditingDetail(detail)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl transition"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Redigér tekst</span>
                  </button>
                )}
                {detail.status === "review" && (
                  <button
                    onClick={() => {
                      const item = detail;
                      setDetail(null);
                      openApprove(item);
                    }}
                    className="px-3 py-1.5 text-xs font-bold bg-[#0F3F34] hover:bg-[#165A4B] text-white rounded-xl transition"
                  >
                    Godkend til buffer
                  </button>
                )}
                <button onClick={() => { setDetail(null); setIsEditingDetail(false); }} className="text-gray-400 hover:text-gray-600" aria-label="Luk">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex gap-2">
                <button
                  onClick={() => setDetailTab("article")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    detailTab === "article" ? "bg-[#0F3F34] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Artikeltekst & sektioner
                </button>
                <button
                  onClick={() => setDetailTab("quality")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    detailTab === "quality" ? "bg-[#0F3F34] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Kvalitetstjek & Historik
                </button>
              </div>
              {engine?.qualityCheck && (
                <button
                  onClick={rerunQualityCheck}
                  disabled={busyId === detail.id}
                  className="flex items-center gap-1 text-xs font-bold text-[#0F3F34] hover:underline disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busyId === detail.id ? "animate-spin" : ""}`} />
                  <span>Kør kvalitetstjek igen</span>
                </button>
              )}
            </div>

            {detailTab === "article" ? (
              isEditingDetail && editDraft ? (
                <div className="space-y-4 text-xs">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div>
                      <label className="block font-bold text-gray-700 mb-1">Titel (H1)</label>
                      <input
                        type="text"
                        value={editDraft.title}
                        onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-gray-700 mb-1">Mål-søgeord</label>
                      <input
                        type="text"
                        value={editDraft.targetKeyword}
                        onChange={(e) => setEditDraft({ ...editDraft, targetKeyword: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-gray-700 mb-1">Meta-titel ({editDraft.metaTitle.length} tegn)</label>
                      <input
                        type="text"
                        value={editDraft.metaTitle}
                        onChange={(e) => setEditDraft({ ...editDraft, metaTitle: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-gray-700 mb-1">Slug</label>
                      <input
                        type="text"
                        value={editDraft.slug}
                        onChange={(e) => setEditDraft({ ...editDraft, slug: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white font-mono"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-bold text-gray-700 mb-1">Meta-beskrivelse ({editDraft.metaDescription.length} tegn)</label>
                      <textarea
                        rows={2}
                        value={editDraft.metaDescription}
                        onChange={(e) => setEditDraft({ ...editDraft, metaDescription: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block font-bold text-gray-700 mb-1">Ingress / Excerpt</label>
                      <textarea
                        rows={2}
                        value={editDraft.excerpt}
                        onChange={(e) => setEditDraft({ ...editDraft, excerpt: e.target.value })}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                      />
                    </div>
                  </div>

                  {/* Sections editor */}
                  <div className="space-y-3">
                    <div className="font-bold text-gray-900 text-sm">Sektioner ({(editDraft.body.sections || []).length})</div>
                    {(editDraft.body.sections || []).map((sec: any, si: number) => (
                      <div key={si} className="p-4 rounded-xl border border-gray-200 space-y-2.5 bg-white">
                        <div>
                          <label className="block font-bold text-gray-600 mb-1">H2 Overskrift #{si + 1}</label>
                          <input
                            type="text"
                            value={sec.heading || ""}
                            onChange={(e) => {
                              const next = [...editDraft.body.sections];
                              next[si] = { ...next[si], heading: e.target.value };
                              setEditDraft({ ...editDraft, body: { ...editDraft.body, sections: next } });
                            }}
                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg font-semibold"
                          />
                        </div>
                        <div>
                          <label className="block font-bold text-gray-600 mb-1">Afsnit (adskilt med tom linje)</label>
                          <textarea
                            rows={Math.max(4, (sec.paragraphs || []).length * 2)}
                            value={(sec.paragraphs || []).join("\n\n")}
                            onChange={(e) => {
                              const next = [...editDraft.body.sections];
                              next[si] = {
                                ...next[si],
                                paragraphs: e.target.value.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
                              };
                              setEditDraft({ ...editDraft, body: { ...editDraft.body, sections: next } });
                            }}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg leading-relaxed"
                          />
                        </div>
                        {sec.bullets && sec.bullets.length > 0 && (
                          <div>
                            <label className="block font-bold text-gray-600 mb-1">Punktopstilling (én pr. linje)</label>
                            <textarea
                              rows={sec.bullets.length + 1}
                              value={sec.bullets.join("\n")}
                              onChange={(e) => {
                                const next = [...editDraft.body.sections];
                                next[si] = {
                                  ...next[si],
                                  bullets: e.target.value.split("\n").map((b) => b.trim()).filter(Boolean),
                                };
                                setEditDraft({ ...editDraft, body: { ...editDraft.body, sections: next } });
                              }}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* FAQs editor */}
                  {(editDraft.body.faqs || []).length > 0 && (
                    <div className="space-y-2.5">
                      <div className="font-bold text-gray-900 text-sm">FAQ ({(editDraft.body.faqs || []).length})</div>
                      {(editDraft.body.faqs || []).map((faq: any, fi: number) => (
                        <div key={fi} className="p-3.5 rounded-xl border border-gray-200 space-y-2 bg-gray-50/50">
                          <input
                            type="text"
                            value={faq.question || ""}
                            onChange={(e) => {
                              const next = [...editDraft.body.faqs];
                              next[fi] = { ...next[fi], question: e.target.value };
                              setEditDraft({ ...editDraft, body: { ...editDraft.body, faqs: next } });
                            }}
                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg font-bold bg-white"
                          />
                          <textarea
                            rows={3}
                            value={faq.answer || ""}
                            onChange={(e) => {
                              const next = [...editDraft.body.faqs];
                              next[fi] = { ...next[fi], answer: e.target.value };
                              setEditDraft({ ...editDraft, body: { ...editDraft.body, faqs: next } });
                            }}
                            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => { setIsEditingDetail(false); setEditDraft(null); }}
                      className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50"
                    >
                      Annuller
                    </button>
                    <button
                      onClick={saveDetailChanges}
                      disabled={busyId === detail.id}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl disabled:opacity-50"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>Gem & kør kvalitetstjek</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5 text-sm text-gray-800">
                  {detail.excerpt && (
                    <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 font-medium leading-relaxed">
                      {detail.excerpt}
                    </div>
                  )}

                  {(detail.body?.takeaways || []).length > 0 && (
                    <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-200 space-y-1.5">
                      <div className="text-xs font-bold uppercase tracking-wider text-emerald-900">Kort fortalt</div>
                      <ul className="list-disc list-inside text-xs text-emerald-950 space-y-1">
                        {((detail.body?.takeaways || []) as string[]).map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-5">
                    {(detail.body?.sections || []).map((sec: any, si: number) => (
                      <div key={si} className="space-y-2.5 border-b border-gray-100 pb-4 last:border-b-0">
                        <h4 className="font-bold text-gray-900 text-base">{sec.heading}</h4>
                        {(sec.paragraphs || []).map((p: string, pi: number) => (
                          <p key={pi} className="text-sm text-gray-700 leading-relaxed">{p}</p>
                        ))}
                        {(sec.bullets || []).length > 0 && (
                          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 pl-1">
                            {sec.bullets.map((b: string, bi: number) => (
                              <li key={bi}>{b}</li>
                            ))}
                          </ul>
                        )}
                        {sec.callout && (
                          <div className="p-3 rounded-lg bg-amber-50/70 border border-amber-200 text-xs text-amber-950">
                            {sec.callout}
                          </div>
                        )}
                        {sec.table && sec.table.rows && (
                          <div className="overflow-x-auto border border-gray-200 rounded-xl mt-2">
                            {sec.table.caption && (
                              <div className="px-3 py-2 bg-gray-50 text-xs font-bold text-gray-700 border-b border-gray-200">
                                {sec.table.caption}
                              </div>
                            )}
                            <table className="w-full text-xs text-left">
                              <thead className="bg-gray-50/70 text-gray-600 border-b border-gray-200">
                                <tr>
                                  {(sec.table.columns || []).map((col: string, ci: number) => (
                                    <th key={ci} className="px-3 py-2 font-bold">{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(sec.table.rows || []).map((r: string[], ri: number) => (
                                  <tr key={ri}>
                                    {r.map((cell: string, ci: number) => (
                                      <td key={ci} className="px-3 py-1.5 text-gray-700">{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {(detail.body?.faqs || []).length > 0 && (
                    <div className="space-y-2.5 pt-2 border-t border-gray-200">
                      <h4 className="font-bold text-gray-900 text-base">Ofte stillede spørgsmål ({(detail.body?.faqs || []).length})</h4>
                      <div className="space-y-2">
                        {((detail.body?.faqs || []) as { question: string; answer: string }[]).map((f, i) => (
                          <div key={i} className="p-3.5 rounded-xl bg-gray-50 border border-gray-100 space-y-1">
                            <div className="font-bold text-gray-900 text-xs">{f.question}</div>
                            <div className="text-xs text-gray-700 leading-relaxed">{f.answer}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(detail.body?.sources || []).length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-gray-200 text-xs">
                      <div className="font-bold text-gray-900">Kilder</div>
                      <ul className="space-y-1">
                        {((detail.body?.sources || []) as { title: string; url: string }[]).map((s, i) => (
                          <li key={i}>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-[#0F3F34] hover:underline inline-flex items-center gap-1">
                              {s.title || s.url} <ExternalLink className="w-3 h-3" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div><span className="text-gray-500">Meta-titel:</span> {detail.metaTitle || "–"}</div>
                  <div><span className="text-gray-500">Forfatter:</span> {detail.author || "–"}{detail.authorVerified ? "" : " (ikke verificeret)"}</div>
                  <div className="col-span-2"><span className="text-gray-500">Meta-beskrivelse:</span> {detail.metaDescription || "–"}</div>
                  {detail.llmUsage && (
                    <div className="col-span-2 text-gray-500">
                      Skrevet af {detail.llmModel}: {Number(detail.llmUsage.input_tokens || 0).toLocaleString("da-DK")} input- /
                      {" "}{Number(detail.llmUsage.output_tokens || 0).toLocaleString("da-DK")} output-tokens, ca. ${Number(detail.llmUsage.cost_usd_estimate || 0).toFixed(2)}
                    </div>
                  )}
                </div>
                {detail.quality && (
                  <div className="space-y-2">
                    <div className="text-xs font-bold text-gray-900">Kvalitetstjek · {detail.quality.score} % · {detail.quality.word_count} ord</div>
                    <QualityList report={detail.quality} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <div className="text-xs font-bold text-gray-900">Historik</div>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {detail.events.map((ev, i) => (
                      <li key={i}>
                        <span className="text-gray-400">{fmtDateTime(ev.created_at)}</span> · {ev.actor} · {ev.event}
                        {ev.to_status ? ` → ${STATUS_LABEL[ev.to_status] || ev.to_status}` : ""}{ev.note ? ` · ${ev.note}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: Opret ny artikel */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#0F3F34]" />
                <span>{engine ? "Ny idé" : "Opret ny artikel"} ({market})</span>
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateArticle} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Artikel Titel *</label>
                <input
                  type="text"
                  required
                  placeholder={market === "NO" ? "f.eks. Refinansiering uten sikkerhet – Komplett guide" : "f.eks. Samla lån utan UC – Komplett guide"}
                  value={newTitle}
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                    if (!newSlug) {
                      setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Mål-Søgeord</label>
                  <input
                    type="text"
                    placeholder={market === "NO" ? "f.eks. refinansiering uten sikkerhet" : "f.eks. samla lån utan uc"}
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Estimeret Månedlig Volumen</label>
                  <input
                    type="number"
                    value={newVolume}
                    onChange={(e) => setNewVolume(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Kategori</label>
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as ContentType)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  >
                    {engine ? (
                      <>
                        <option value="guide">Guide</option>
                        <option value="pillar">Pillar-guide</option>
                        <option value="lender_review">Långiver-anmeldelse</option>
                        <option value="comparison">Sammenligning</option>
                        <option value="news">Nyhed</option>
                      </>
                    ) : (
                      <>
                        <option value="guide">Guide</option>
                        <option value="samla">Samlelån</option>
                        <option value="belopp">Beløbsside</option>
                        <option value="lender-review">Långiver Review</option>
                        <option value="nyhed">Nyhed</option>
                      </>
                    )}
                  </select>
                </div>
                {!engine && (
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">Human Polish Niveau</label>
                    <select
                      value={newEditLevel}
                      onChange={(e) => setNewEditLevel(e.target.value as ContentItem["aiEditLevel"])}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                    >
                      <option value="heavy">Heavy Polish (Menneske fact-check)</option>
                      <option value="medium">Standard Polish</option>
                      <option value="light">Light Edit</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">URL Slug</label>
                <input
                  type="text"
                  placeholder={market === "NO" ? "refinansiering-uten-sikkerhet" : "samla-lan-utan-uc"}
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">{engine ? "Vinkel og fakta til skribenten" : "Noter / Vinkel"}</label>
                <textarea
                  rows={engine ? 3 : 2}
                  placeholder={engine ? "Vinkel, målgruppe og dokumenterede fakta (fx långiverens renter og gebyrer med kilde og dato)" : "Ekstra noter om målgruppe eller CTA..."}
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl transition"
                >
                  {engine ? "Opret idé" : "Opret og tilføj til Buffer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
